import React, { useContext, useRef, useState, useEffect } from 'react';
import styles from './style.module.scss';
import { toast } from 'react-toastify';
import useAPI from '../../../hooks/useAPI';
import TokenListContext from '../../../contexts/TokenListContext';
import WalletContext from '../../../contexts/WalletContext';
import DownloadIcon from '../../../assets/downloadIcon.png';
import BulkTokenTransferModal from './BulkTokenTransferModal';

type BulkTokenTransferProps = {
  onClose: () => void;
  setActiveTab?: (tab: 'Individual' | 'Bulk') => void;
};

const MAX_FILE_SIZE_MB = 10;
const MAX_ROWS = 10000;
const REQUIRED_HEADERS = ['Email', 'Amount'];

const BulkTokenTransfer: React.FC<BulkTokenTransferProps> = ({
  onClose,
  setActiveTab = (tab: 'Individual' | 'Bulk') => {},
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [csvRows, setCsvRows] = useState<any[]>([]);
  const { tokenList } = useContext(TokenListContext);
  const { refetchTransactions, refetch: walletBalanceRefetch } =
    useContext(WalletContext);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploadDisabled, setIsUploadDisabled] = useState(false);

  // Debug: Log when modal is shown
  useEffect(() => {
    if (showPreview) {
      console.log('Modal shown with data:', csvRows);
    }
  }, [showPreview, csvRows]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setError('');

    if (!file) return;

    const isCSV = file.name.toLowerCase().endsWith('.csv');
    const isTooLarge = file.size > MAX_FILE_SIZE_MB * 1024 * 1024;

    if (!isCSV) {
      setError('Only .csv files are allowed.');
      setCsvFile(null);
      return;
    }

    if (isTooLarge) {
      setError('File size must be 10MB or less.');
      setCsvFile(null);
      return;
    }

    e.target.value = '';
    const text = await file.text();
    const lines = text
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);

    if (lines.length < 2) {
      setError('CSV must have at least one row of data.');
      setCsvFile(null);
      return;
    }

    const rawHeaders = lines[0].split(',').map(header => header.trim());

    const hasAllHeaders = REQUIRED_HEADERS.every(req =>
      rawHeaders.includes(req),
    );

    if (!hasAllHeaders) {
      const missingHeaders = REQUIRED_HEADERS.filter(
        req => !rawHeaders.includes(req),
      );
      setError(
        `CSV is missing required header(s): ${missingHeaders.join(', ')}. Headers are case-sensitive. Please use the exact template.`,
      );
      setCsvFile(null);
      return;
    }

    // Check for extra headers beyond the required ones
    const extraHeaders = rawHeaders.filter(
      header => !REQUIRED_HEADERS.includes(header),
    );

    if (extraHeaders.length > 0) {
      setError(
        `CSV contains extra column(s): ${extraHeaders.join(', ')}. Only 'Email' and 'Amount' columns are allowed. Please use the exact template.`,
      );
      setCsvFile(null);
      return;
    }

    const dataRows: any[] = [];
    let blankRowFound = false;

    lines
      .slice(1)
      .filter(line => {
        return line.split(',').some(allLine => {
          return allLine.trim() !== '';
        });
      })
      .forEach((row, rowIndex) => {
        const values = row.split(',');
        const obj: any = {};

        rawHeaders.forEach((h, i) => {
          obj[h] = values[i]?.trim().replace(/^"|"$/g, '');
        });

        const isEmptyRow = REQUIRED_HEADERS.some(h => {
          const value = obj[h];
          return !value || value.trim() === '';
        });

        if (isEmptyRow) {
          blankRowFound = true;
          return;
        }

        dataRows.push(obj);
      });

    if (blankRowFound) {
      setError(
        'CSV contains rows with missing required fields. Please fill all columns.',
      );
      setCsvFile(null);
      return;
    }

    // Validate email format
    const invalidEmails = dataRows.filter(row => {
      const email = row['Email']?.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !emailRegex.test(email);
    });

    if (invalidEmails.length > 0) {
      setError(
        'Invalid email format found in CSV. Please check all email addresses.',
      );
      setCsvFile(null);
      return;
    }

    // Validate amount is positive number
    const invalidAmounts = dataRows.filter(row => {
      const amount = row['Amount']?.trim();
      const amountNum = parseFloat(amount);
      return isNaN(amountNum) || amountNum <= 0;
    });

    if (invalidAmounts.length > 0) {
      setError(
        'Invalid amounts found in CSV. All amounts must be positive numbers.',
      );
      setCsvFile(null);
      return;
    }

    // Check for duplicate emails with different amounts
    const emailMap = new Map<string, { amount: string; row: number }>();
    let hasDuplicateEmail = false;
    let duplicateEmailDetails = '';

    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i];
      const email = row['Email']?.trim().toLowerCase();
      const amount = row['Amount']?.trim();

      if (emailMap.has(email)) {
        const existing = emailMap.get(email)!;
        if (existing.amount !== amount) {
          hasDuplicateEmail = true;
          duplicateEmailDetails = `Email: ${email} appears in rows ${existing.row + 1} and ${i + 1} with different amounts (${existing.amount} and ${amount})`;
          break;
        }
      } else {
        emailMap.set(email, { amount, row: i });
      }
    }

    if (hasDuplicateEmail) {
      setError(
        `Duplicate email found with different amounts: ${duplicateEmailDetails}`,
      );
      setCsvFile(null);
      return;
    }

    if (dataRows.length > MAX_ROWS) {
      setError('CSV exceeds maximum row limit (10,000).');
      setCsvFile(null);
      return;
    }

    console.log('Parsed CSV data:', dataRows);
    setCsvRows(dataRows);
    setCsvFile(file);
    setIsUploadDisabled(true);
    toast.success(`File "${file.name}" ready for upload.`);
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const resetUpload = () => {
    setCsvFile(null);
    setCsvRows([]);
    setShowPreview(false);
  };

  const { refetch: bulkTransfer } = useAPI({
    url: `${process.env.REACT_APP_API_BASE_URL}/token/bulk/transfer`,
    method: 'POST',
    isFormData: true,
    autoFetch: false,
    onSuccess: (res: any) => {
      const transferredCount = res?.result?.count || 0;
      toast.success(
        `Successfully transferred tokens to ${transferredCount} recipient${transferredCount !== 1 ? 's' : ''}!`,
      );
      // Refresh transaction list and wallet balance
      refetchTransactions();
      walletBalanceRefetch();
      resetUpload();
      onClose();
      if (typeof setActiveTab === 'function') {
        setActiveTab('Individual');
      }
    },
    onError: (err: any) => {
      const serverErrors = err?.response?.data?.errors;

      if (Array.isArray(serverErrors) && serverErrors.length > 0) {
        // Check if specific validation errors are present
        const allMessages = serverErrors.flatMap((e: any) => e.errors);
        const hasEmailConflict = allMessages.some((msg: string) =>
          msg.toLowerCase().includes('not valid email'),
        );
        const hasWalletNotFound = allMessages.some((msg: string) =>
          msg.toLowerCase().includes('wallet is not found'),
        );
        const hasAmountError = allMessages.some((msg: string) =>
          msg.toLowerCase().includes('amount must be a positive number'),
        );

        if (hasEmailConflict || hasWalletNotFound || hasAmountError) {
          let customMsg = 'Validation failed: ';
          if (hasEmailConflict) customMsg += 'Invalid email format, ';
          if (hasWalletNotFound) customMsg += 'Wallet not found, ';
          if (hasAmountError) customMsg += 'Invalid amount, ';
          customMsg = customMsg.slice(0, -2) + '.'; // Remove last comma and space
          toast.error(customMsg);
        } else {
          // Fallback for other server validation errors
          toast.error('Validation failed. Please check the CSV data.');
        }
      } else {
        // Generic error fallback
        toast.error(
          'Upload failed: ' + (err?.response?.data?.message || 'Unknown error'),
        );
      }

      // Reset tab to 'Individual'
      if (typeof setActiveTab === 'function') {
        setActiveTab('Individual');
      }
    },
  });

  const handleBulkTransfer = () => {
    if (!csvFile || !tokenList[0]?.tokenId) {
      toast.error('Please select a file and ensure tokens are available');
      return;
    }

    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('tokenId', tokenList[0].tokenId);

    toast.promise(bulkTransfer(formData), {
      pending: 'Transferring tokens...',
    });
  };

  const downloadTemplate = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDownloading) {
      console.log('Download already in progress, ignoring click');
      return;
    }

    console.log('Starting download...');
    setIsDownloading(true);

    try {
      const csvContent =
        'Email,Amount\nuser@example.com,100\nanother@example.com,50';
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bulk-token-transfer-template.csv';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      console.log('Download completed successfully');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to download template');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={styles.bulkUploadContainer}>
      {!showPreview && (
        <>
          <p className={styles.infoText}>
            Seamlessly distribute tokens to multiple employees at once - upload
            to complete payouts in just a few clicks.
          </p>

          <span className={styles.uploadLabel}>
            Upload file with transfer details
          </span>

          <div className={styles.uploadSection}>
            <label
              htmlFor="csvUpload"
              className={`${styles.uploadBox} ${
                isUploadDisabled ? styles.disabledUploadBox : ''
              }`}
            >
              <div className={styles.uploadIconContainer}>
                <span className={styles.uploadIcon}>
                  <img
                    src={DownloadIcon}
                    alt="upload icon"
                    width={100}
                    height={100}
                  />
                </span>
                <div className={styles.uploadTextContainer}>
                  <p className={styles.uploadText}>
                    Select a file or drag & drop here
                  </p>
                  <p className={styles.fileFormat}>
                    File Format: CSV (up to 10MB, max 10,000 rows)
                  </p>
                </div>
                <button
                  className={
                    isUploadDisabled
                      ? styles.uploadBtnDisabled
                      : styles.uploadBtn
                  }
                  type="button"
                  onClick={handleUpload}
                  disabled={isUploadDisabled}
                >
                  Upload
                </button>
              </div>
              <input
                id="csvUpload"
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className={styles.hiddenInput}
                onChange={handleFileChange}
              />
            </label>
          </div>

          {error && (
            <div
              className={styles.errorText}
              style={{ whiteSpace: 'pre-line' }}
            >
              {error}
            </div>
          )}

          {csvFile && (
            <div className={styles.fileDetailsContainer}>
              <div className={styles.uploadProgress}>
                <div className={styles.uploadedFile}>
                  <div className={styles.fileInfo}>
                    <span className={styles.fileIcon}>📄</span>
                    <span className={styles.fileName}>{csvFile.name}</span>
                    <span className={styles.fileSize}>
                      {(csvFile.size / 1024).toFixed(2)} KB
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={() => {
                      setCsvFile(null);
                      setIsUploadDisabled(false);
                    }}
                  >
                    x
                  </button>
                </div>
              </div>
              <div className={styles.proceedContainer}>
                <button
                  className={styles.proceedBtn}
                  type="button"
                  onClick={() => setShowPreview(true)}
                >
                  Proceed
                </button>
              </div>
            </div>
          )}
          <div className={styles.templateDownload}>
            <a
              href="/template/bulk-token-transfer-template.csv"
              className={styles.downloadTemplate}
              //   onClick={handleDownloadTemplate}
            >
              {isDownloading ? (
                <span className={styles.spinner}></span>
              ) : (
                'Download Template'
              )}
            </a>
          </div>
        </>
      )}

      <BulkTokenTransferModal
        data={csvRows}
        show={showPreview}
        onClose={onClose}
        onCancel={() => setShowPreview(false)}
        onSend={handleBulkTransfer}
      />
    </div>
  );
};

export default BulkTokenTransfer;
